import type {
  SkiElevationData,
  ElevationTier,
  VisibilityLevel,
  SkiQualityTag,
} from "@/lib/conditions/ski-elevation";
import { scoreBadgeColor } from "@/lib/conditions/scoring";
import { scoreOutOf100 } from "@/lib/conditions/score-display";
import { coverWord } from "@/app/_components/conditions/ski/ski-v3-words";

function snowTypeColor(type: ElevationTier["snowType"]): string {
  switch (type) {
    case "fresh-powder":
      return "bg-emerald-100 text-emerald-800 border-emerald-200";
    case "packed-powder":
      return "bg-sky-100 text-sky-800 border-sky-200";
    case "spring-snow":
      return "bg-amber-100 text-amber-800 border-amber-200";
    case "heavy-wet":
      return "bg-orange-100 text-orange-800 border-orange-200";
    case "icy":
      return "bg-slate-100 text-slate-700 border-slate-200";
    case "wind-affected":
      return "bg-red-100 text-red-800 border-red-200";
    case "no-snow":
    case "unknown":
      return "bg-slate-50 text-slate-500 border-slate-200";
  }
}

function visibilityColor(level: VisibilityLevel): string {
  switch (level) {
    case "excellent":
      return "bg-emerald-400";
    case "good":
      return "bg-sky-400";
    case "moderate":
      return "bg-amber-400";
    case "poor":
      return "bg-orange-400";
    case "very-poor":
      return "bg-red-400";
  }
}

function tagBg(color: SkiQualityTag["color"]): string {
  switch (color) {
    case "emerald":
      return "bg-emerald-100 text-emerald-800";
    case "amber":
      return "bg-amber-100 text-amber-800";
    case "red":
      return "bg-red-100 text-red-800";
    case "sky":
      return "bg-sky-100 text-sky-800";
    case "slate":
      return "bg-slate-100 text-slate-700";
    case "violet":
      return "bg-violet-100 text-violet-800";
  }
}

/** A band score on the 0 to 10 scale; null with a label ("Closed") when the score is withheld. */
type TierScore = { score: number | null; label: string };

function coverChipColor(word: string): string {
  if (word === "Full cover") return "bg-emerald-50 text-emerald-800 border-emerald-200";
  if (word === "Thin in places" || word === "Marginal") return "bg-amber-50 text-amber-800 border-amber-200";
  return "bg-red-50 text-red-800 border-red-200";
}

function ElevationRow({
  tier,
  score,
  showDash,
}: {
  tier: ElevationTier;
  score?: TierScore;
  /** When true and score is absent, render a muted "No score" placeholder in the score column. */
  showDash?: boolean;
}) {
  const badge = score != null && score.score != null ? scoreBadgeColor(score.score) : null;
  // Ski v3: the engine's cover word for this band (absent on revision 2 rows).
  const cover = coverWord(tier.coverWord);

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-0 py-4 border-b border-border-light last:border-b-0">
      <div className="sm:w-[120px] shrink-0">
        <div className="text-[11px] font-semibold uppercase tracking-[1.2px] text-text-muted">
          {tier.label}
        </div>
        <div className="text-[13px] font-semibold text-navy tabular-nums">
          {tier.elevationM.toLocaleString()} m
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 sm:w-[180px] shrink-0">
        <div className="text-center">
          <div className="text-[18px] font-serif font-bold text-navy tabular-nums leading-none">
            {tier.tempC.toFixed(0)}°
          </div>
          <div className="text-[10px] text-text-muted mt-0.5">°C</div>
        </div>
        <div className="text-center">
          <div className="text-[14px] font-semibold text-navy tabular-nums leading-none">
            {tier.windSpeedKmh}
          </div>
          <div className="text-[10px] text-text-muted mt-0.5">km/h</div>
        </div>
        <span
          className={`inline-block rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${snowTypeColor(tier.snowType)}`}
        >
          {tier.snowTypeLabel}
        </span>
        {cover && (
          <span
            data-tier-cover=""
            className={`inline-block rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${coverChipColor(cover)}`}
          >
            {cover}
          </span>
        )}
      </div>

      <div className="flex-1 text-[12.5px] text-text-body leading-snug sm:pl-4">
        {tier.tacticalNote}
      </div>

      {badge != null && score != null && score.score != null ? (
        <div className="flex items-center gap-1.5 sm:w-[90px] shrink-0 sm:justify-end">
          <div
            className={`inline-flex items-center gap-1 rounded-[6px] px-2 py-0.5 ${badge.bg} ${badge.text}`}
          >
            <span aria-hidden="true">●</span>
            <span className="text-[13px] font-bold tabular-nums leading-none">
              {scoreOutOf100(score.score)}
            </span>
          </div>
          <span className="text-[11px] text-text-muted">{score.label}</span>
        </div>
      ) : score != null ? (
        <div className="flex items-center sm:w-[90px] shrink-0 sm:justify-end">
          <span className="text-[12px] font-semibold text-navy">{score.label}</span>
        </div>
      ) : showDash === true ? (
        <div className="sm:w-[90px] shrink-0 sm:justify-end flex sm:flex sm:items-center">
          <span className="text-[12px] text-text-muted">No score</span>
        </div>
      ) : null}
    </div>
  );
}

export function SkiElevationPanel({
  data,
  tierScores = null,
  bandsSportLabel = null,
}: {
  data: SkiElevationData;
  tierScores?: Record<"Base" | "Mid" | "Summit", TierScore> | null;
  bandsSportLabel?: string | null;
}) {
  return (
    <div className="bg-white rounded-[16px] border border-border-light p-5 md:p-7">
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-[12px] font-semibold uppercase tracking-[1.2px] text-text-muted">
          Conditions by elevation
        </h3>
        <div className="flex items-center gap-3">
          {bandsSportLabel != null && (
            <span className="text-[11px] text-text-muted italic">
              {bandsSportLabel} score per band
            </span>
          )}
          <span className="text-[11px] text-text-muted tabular-nums">
            Freezing level: {data.freezingLevelM.toLocaleString()} m
          </span>
        </div>
      </div>

      <div>
        {data.tiers.map((tier) => {
          const tierScore = tierScores != null ? (tierScores[tier.label] ?? null) : null;
          return (
            <ElevationRow
              key={tier.label}
              tier={tier}
              score={tierScore ?? undefined}
              showDash={bandsSportLabel != null && tierScore == null}
            />
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mt-4 pt-4 border-t border-border-light">
        <span
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-700 capitalize"
          title={data.visibilityLabel}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${visibilityColor(data.visibility)}`} />
          {data.visibility.replace("-", " ")} visibility
        </span>
        {data.qualityTags.map((tag) => (
          <span
            key={tag.text}
            className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${tagBg(tag.color)}`}
          >
            {tag.text}
          </span>
        ))}
      </div>
    </div>
  );
}
